/**
 * Code-defined tooltip content for Portfolio Overview Grid column headers.
 * Supplies the "data source" and "calculation formula" that no
 * FieldDefinition / ColumnMeta / backend field currently carries, plus a
 * plain-language description for universal fields.
 *
 * Source of truth: the "Metrics Labels/Descriptions" catalog
 * (foresightdata Notion, generated from `ForesightData/api`
 * `src/metrics/metrics.service.ts` + this repo's `metrics.data-model.ts`).
 *   - `description` uses the catalog's "Accounting Suggestion" (its designated
 *     recommended user-facing tooltip, reviewed against ASC 946 / ILPA).
 *   - `formula` uses the catalog's "Code Formula" (what the metrics service
 *     actually computes).
 *   - `source` is "Calculated" (`CALCULATED`) for every metric: the catalog
 *     documents these as computed by the Foresight metrics service, not
 *     sourced per-field. This uniform value is retained as documentation but
 *     intentionally NOT rendered in the tooltip (it's low-signal on every
 *     header) — `buildHeaderTooltip` suppresses it. Meaningful, distinguishing
 *     sources (e.g. General Ledger / Aumni / User Input for custom or
 *     non-computed fields) DO render. (Company identity columns below are not
 *     metrics and are excluded from the catalog, so they keep their own
 *     origin, which renders.)
 *
 * Keyed by column identity — `cm.path || cm.name` — the same key used to look
 * up the FieldDefinition in `columnsMetaToColumnsDef`. Custom fields are not
 * listed here: their descriptions continue to flow from the customer-provided
 * `FieldDefinition.description` via the fallback in `buildHeaderTooltip`.
 *
 * This map is additive: more of the catalog's metrics can be seeded later with
 * no other code changes. Columns with no entry (and no fallback description)
 * show no tooltip.
 */
export interface FieldTooltipContent {
  /**
   * Plain-language description. Overrides the FieldDefinition/ColumnMeta
   * description when present (universal fields are code-defined here).
   */
  description?: string;
  /** Data origin, e.g. 'General Ledger', 'Aumni', 'User Input', 'Calculated'. */
  source?: string;
  /** Human-readable calculation, e.g. 'Total Value ÷ Paid-In Capital'. */
  formula?: string;
}

/**
 * Uniform source for computed metrics. Retained on entries as documentation
 * but suppressed from the rendered tooltip by `buildHeaderTooltip` (see there).
 */
export const CALCULATED = 'Calculated';
const USER_INPUT = 'User Input';
const CRUNCHBASE = 'Crunchbase';

export const FIELD_TOOLTIP_CONTENT: Readonly<Record<string, FieldTooltipContent>> = {
  // --- Company identity (predefined columns) ---
  // Keyed by predefined-column identity: CompanyNameAndLogo has path '' so its
  // key is its name; the ROUND_TRACKER "Company Name" column has path 'company'.
  CompanyNameAndLogo: {
    description: 'The portfolio company.',
    source: `${CRUNCHBASE} / ${USER_INPUT}`,
  },
  company: {
    description: 'The portfolio company.',
    source: `${CRUNCHBASE} / ${USER_INPUT}`,
  },

  // --- Investment performance & returns ---
  // Keys are the company-level metric paths on the Portfolio Overview grid;
  // leaf names match IMetricsDataModel fields and the catalog's Metric Key.
  // Fund-level metrics (netIRR, lp*, contributions) live on the separate Fund
  // grid and are intentionally NOT seeded here.
  'metrics.irr': {
    description:
      'XIRR of all investment cashflows (negative), distribution cashflows (positive), and current FMV as terminal value, expressed as %. Returns null if calculation cannot converge.',
    source: CALCULATED,
    formula:
      'XIRR of cashflows: −investment per transaction, +distributions per transaction, +FMV as terminal value',
  },
  'metrics.moic': {
    description:
      '(FMV + Distributions + Escrow) / Amount Invested. Amount Invested is cash capital deployed only (excludes capitalized interest, PIK, OID). Includes escrow — see TVPI for the escrow-excluded variant.',
    source: CALCULATED,
    formula: '(FMV + Distributions + Escrow Amount) / Amount Invested',
  },
  'metrics.tvpi': {
    description:
      '(FMV + Distributions) / Amount Invested. ILPA standard. Amount Invested is cash deployed (excludes non-cash accruals). Does not include escrow (see MOIC for the escrow-inclusive variant).',
    source: CALCULATED,
    formula: '(FMV + Distributions) / Amount Invested',
  },
  'metrics.dpi': {
    description:
      'Distributions / Amount Invested. ILPA standard. Amount Invested is cash deployed (excludes non-cash accruals). Measures cash-on-cash return to LPs.',
    source: CALCULATED,
    formula: 'Distributions / Amount Invested',
  },
  'metrics.rvpi': {
    description:
      'FMV / Amount Invested. ILPA standard. Amount Invested is cash deployed (excludes non-cash accruals). Measures remaining unrealized value relative to capital deployed.',
    source: CALCULATED,
    formula: 'FMV / Amount Invested',
  },

  // --- Valuation ---
  'metrics.fmv': {
    description: 'Sum of current fair market value across all positions. Per ASC 820 fair value hierarchy.',
    source: CALCULATED,
    formula: 'Sum of current FMV across all transactions (floored at 0)',
  },
  'metrics.totalValue': {
    description:
      'FMV + Distributions + Escrow. Total value generated by the investment including unrealized and realized components.',
    source: CALCULATED,
    formula: 'FMV + Distributions + Escrow Amount',
  },

  // --- Investment amount & cost basis ---
  'metrics.amountInvested': {
    description:
      'Total cash capital deployed. Includes cash investments, special contributions, and restructure-ins. Excludes non-cash accruals (capitalized interest, PIK, OID). This is the denominator for MOIC/TVPI/DPI/RVPI. See Total Cost for the full ASC 946 cost basis including non-cash accruals.',
    source: CALCULATED,
    formula:
      'Sum of cash investmentAmount across transactions (Investment − Cost, Special Contribution, Restructure In)',
  },
  'metrics.initialInvestment': {
    description:
      'Amount deployed in the initial investment. Identified by first investment round or first 15-day investment window.',
    source: CALCULATED,
    formula: 'Sum of investment amounts from the first investment round',
  },

  // --- Distributions ---
  'metrics.distributions': {
    description:
      'Total cash distributed to the fund from this investment (proceeds, dividends, interest payments).',
    source: CALCULATED,
    formula: 'Sum of distributions across all transactions',
  },
  'metrics.cashInterestReceived': {
    description:
      'Interest collected in cash on notes/loans. Income only — stays in distributions but is netted out of realized G/L and realized MOIC; never affects cost basis.',
    source: CALCULATED,
    formula: 'Sum of Income-Cash Interest entries',
  },
  'metrics.cashDividendReceived': {
    description:
      'Cash dividends received whose cash leg is also booked as proceeds. Income only — stays in distributions but is netted out of realized G/L and realized MOIC; never affects cost basis.',
    source: CALCULATED,
    formula: 'Sum of Income-Cash Dividend entries',
  },

  // --- Status & classification ---
  'metrics.investmentAge': {
    description: 'Years since initial investment. For realized companies, measured to exit date.',
    source: CALCULATED,
    formula: 'Years from initial investment date to as-of date (or last exit date if fully exited)',
  },

  // --- Round Tracker: SAFE/note terms ---
  // Keyed by RoundTracker field path. Blank cells mean the round carries no such term
  // (priced equity, or a note without that term) — never rendered as 0.
  valuationCap: {
    description:
      "Valuation cap on the round's SAFE or convertible note terms. Blank for priced-equity rounds and notes without a cap.",
    source: 'Aumni / Carta / Deal Docs / User Input',
  },
  interestRate: {
    description: "Coupon on the round's convertible note terms. Blank for SAFEs and priced-equity rounds.",
    source: 'Aumni / Carta / Deal Docs / User Input',
  },
  discountRate: {
    description:
      "Conversion discount on the round's SAFE or convertible note terms. Blank for priced-equity rounds and notes without a discount.",
    source: 'Aumni / Carta / Deal Docs / User Input',
  },
};

export function getFieldTooltipContent(key: string | undefined): FieldTooltipContent | undefined {
  if (!key) {
    return undefined;
  }
  return FIELD_TOOLTIP_CONTENT[key];
}
